import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { resolveImagePath, FALLBACK_IMAGE } from "./imagePath";

export default function ItemDetailsScreen() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // console.log("ItemDetailsScreen: Component mounted/re-rendered. ID from useParams:", id);

  useEffect(() => {
    // console.log("ItemDetailsScreen: useEffect triggered. Current ID:", id);
    setIsLoading(true);
    setError(null);

    const fetchItem = async () => {
      if (!id) {
        // console.log("ItemDetailsScreen: fetchItem - No ID provided, setting error.");
        setError("Item ID is missing.");
        setItem(null);
        setIsLoading(false);
        return;
      }
      if (!/^\d+$/.test(id)) {
        // console.log("ItemDetailsScreen: fetchItem - Invalid ID format:", id);
        setError("Invalid Item ID format.");
        setItem(null);
        setIsLoading(false);
        return;
      }

      try {
        // console.log("ItemDetailsScreen: fetchItem - Attempting to fetch item with ID:", id);

        // Retrieve API key from environment variable
        const apiKey = process.env.REACT_APP_FOODGYDE_API_KEY; // Same as used in ItemListScreen

        const response = await axios.get(
          `https://api.foodgyde.com/api/item/${id}`,
          {
            headers: {
              "x-api-key": apiKey
            }
          }
        );
        
        // console.log("ItemDetailsScreen: fetchItem - Axios response received:", response);
        // console.log("ItemDetailsScreen: fetchItem - Fetched item data (response.data):", response.data);
        
        if (response.data && typeof response.data === 'object' && Object.keys(response.data).length > 0 && typeof response.data.id !== 'undefined') {
          // console.log("ItemDetailsScreen: fetchItem - Valid item data received, setting item.");
          setItem(response.data);
        } else {
          // console.error("ItemDetailsScreen: fetchItem - Fetched data is not a valid item object, or item not found, or id missing in data. Response data:", response.data);
          setError("Item not found or data from server is invalid.");
          setItem(null);
        }
      } catch (err) {
        // console.error("ItemDetailsScreen: fetchItem - Error during axios.get (catch block):", err.response || err.message || err);
        setError("Unable to fetch item details. Please check network connection or API status.");
        setItem(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchItem();

  }, [id]);

  // console.log("ItemDetailsScreen: Rendering component. Current item state:", item, "Current error state:", error, "Is Loading:", isLoading);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center">
        <header className="w-full bg-blue-600 p-4 text-white text-center text-lg font-bold">Item Details</header>
        <main className="p-4 max-w-2xl mx-auto"><div className="p-4 text-gray-500">Loading item details for ID: {id}...</div></main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center">
        <header className="w-full bg-blue-600 p-4 text-white text-center text-lg font-bold">Item Details</header>
        <main className="p-4 max-w-2xl mx-auto">
          <div className="p-4 text-red-500">Error: {error}</div>
          <button onClick={() => window.history.back()} className="mt-4 px-6 py-2 bg-gray-600 text-white rounded-md shadow hover:bg-gray-700">Back</button>
        </main>
      </div>
    );
  }
  
  if (!item) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center">
        <header className="w-full bg-blue-600 p-4 text-white text-center text-lg font-bold">Item Details</header>
        <main className="p-4 max-w-2xl mx-auto">
          <div className="p-4 text-gray-500">Item with ID: {id} not found.</div>
           <button onClick={() => window.history.back()} className="mt-4 px-6 py-2 bg-gray-600 text-white rounded-md shadow hover:bg-gray-700">Back</button>
        </main>
      </div>
    );
  }

  const imagePathsFromDB = item.imagePath ? item.imagePath.split(",").map(img => img.trim()) : [];
  const images = imagePathsFromDB.map(resolveImagePath).slice(0, 6);

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      images.length === 0 ? 0 : (prevIndex === 0 ? images.length - 1 : prevIndex - 1)
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      images.length === 0 ? 0 : (prevIndex === images.length - 1 ? 0 : prevIndex + 1)
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="w-full bg-blue-600 p-4 text-white text-center text-lg font-bold">
        Item Details
      </header>
      <main className="p-4 max-w-2xl mx-auto bg-white rounded-md shadow">
        {images.length > 0 && images[currentImageIndex] ? (
          <div className="relative w-full flex items-center justify-center mb-4">
            <button
              onClick={handlePrevImage}
              disabled={images.length <= 1}
              className="absolute left-0 z-10 p-2 m-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 disabled:opacity-25"
            >◀</button>
            <img
              src={images[currentImageIndex]}
              alt={`${item.name || 'Item'} image ${currentImageIndex + 1}`}
              className="w-full max-w-md h-auto max-h-96 object-contain rounded-md"
              onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_IMAGE; console.warn(`Failed to load image: ${images[currentImageIndex]}`);}}
            />
            <button
              onClick={handleNextImage}
              disabled={images.length <= 1}
              className="absolute right-0 z-10 p-2 m-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 disabled:opacity-25"
            >▶</button>
          </div>
        ) : (
          item.imagePath && <div className="text-gray-500 mb-4 text-center">No images available or issue loading images.</div>
        )}
        <h1 className="text-3xl font-bold mb-2">{item.name || "Name not available"}</h1>
        <p className="text-gray-700 mt-2 mb-4">{item.description || "No description."}</p>
        <div className="my-4 border-t pt-4">
            <p className="text-2xl text-blue-600 font-semibold">
            {item.price ? `$${parseFloat(item.price).toFixed(2)}` : "Price not available"}
            </p>
            <p className="text-gray-600 mt-1 text-sm">
            <strong>Barcode:</strong> {item.barcode || "N/A"}
            </p>
        </div>
        {item.ingredients && (
          <div className="mt-4 border-t pt-4">
            <h3 className="text-xl font-semibold text-gray-800 mb-1">Ingredients</h3>
            <p className="text-gray-600">{item.ingredients}</p>
          </div>
        )}
        {item.nutritionFacts && (
          <div className="mt-4 border-t pt-4">
            <h3 className="text-xl font-semibold text-gray-800 mb-1">Nutrition Facts</h3>
            <p className="text-gray-600">{item.nutritionFacts}</p>
          </div>
        )}
        <button
          onClick={() => window.history.back()}
          className="mt-6 w-full px-6 py-3 bg-gray-600 text-white rounded-md shadow hover:bg-gray-700"
        >
          Back
        </button>
      </main>
    </div>
  );
}